<script lang="ts" setup>
import { inject } from 'vue'
import { useChapterTheme } from '../composables/useChapterTheme'
import type { ChapterConfig } from '@/composables/payment'

interface Props {
  value: boolean
  chapter: any
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'prev-chapter'): void
  (e: 'next-chapter'): void
  (e: 'open-toc'): void
}>()

const config = inject<ChapterConfig>('chapterConfig')!
const { theme, THEME_FONT_MAP, THEME_BACKGROUNDS, onChangeThemeSize } = useChapterTheme()

function handlePrev() {
  emit('prev-chapter')
}

function handleNext() {
  emit('next-chapter')
}

function handleOpenTOC() {
  emit('open-toc')
}
</script>

<template>
  <BasicPopup :value="value" :mask="false" @input="emit('input', $event)">
    <div class="w-full px-5 pt-25px py-10" :style="`background: ${theme.background}`">
      <!-- 字体大小调节 -->
      <div class="mb-5 flex gap-4 items-center justify-between">
        <p class="text-14px text-#999 whitespace-nowrap">
          {{ config.texts.themeSizeLabel }}
        </p>
        <div class="flex-1 flex items-center justify-between">
          <span
            class="w-full h-35px lh-35px text-center text-14px bg-#0000000A b-rd-tl-18px b-rd-bl-18px"
            :class="{ 'text-#999': theme.size === '24' }" @click="onChangeThemeSize(-1)"
          >
            {{ config.texts.fontSizeDecrease }}
          </span>
          <span class="flex justify-between items-center w-full h-35px lh-35px min-w-5 text-15px text-center bg-#0000000A">
            <span class="text-#0000001A">|</span>
            <span>{{ theme.size }}</span>
            <span class="text-#0000001A">|</span>
          </span>
          <span
            class="w-full h-35px lh-35px text-center text-14px bg-#0000000A b-rd-tr-18px b-rd-br-18px"
            :class="{ 'text-#999': theme.size === '50' }" @click="onChangeThemeSize(1)"
          >
            {{ config.texts.fontSizeIncrease }}
          </span>
        </div>
      </div>

      <!-- 主题颜色选择 -->
      <div class="flex space-x-25px items-center">
        <div class="flex flex-1 justify-center space-x-20px">
          <span
            v-for="item in THEME_BACKGROUNDS" :key="item" class="w-30px h-30px border rounded-full"
            :style="`background: ${item};`"
            :class="item === theme.background ? 'border-2 border-n-primary' : 'border-#ccc'"
            @click="theme.background = item"
          />
        </div>
      </div>

      <!-- 导航按钮 -->
      <div class="mt-5 flex items-center justify-between">
        <span
          class="theme-btn-bg w-115px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
          :class="{ 'text-#999': !chapter?.prev }" @click="handlePrev"
        >
          {{ config.texts.prevButton }}
        </span>
        <span
          class="theme-btn-bg w-80px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
          @click.stop="handleOpenTOC"
        >
          {{ config.texts.catalogueButton }}
        </span>
        <span
          class="theme-btn-bg w-115px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
          @click="handleNext"
        >
          {{ config.texts.nextButton }}
        </span>
      </div>
    </div>
  </BasicPopup>
</template>
